import React, { useMemo, useState } from 'react'
import { appInsights } from 'config/appInsights'
import { useTheme, useMediaQuery } from '../../contexts/ThemeProvider'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import AppsIcon from '@mui/icons-material/Apps'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import logoConnectDark from '../../assets/images/logo-connect-dark.svg'
import logoTituloConnectDark from '../../assets/images/logo-texto-connect-dark.svg'
import logoConnectLight from '../../assets/images/logo-connect-light.svg'
import logoTituloConnectLight from '../../assets/images/logo-texto-connect-light.svg'
import { Divider, IconButton, Typography, Skeleton } from '@mui/material'
import { Tooltip } from '@vale/agata-components-react'
import {
  HeaderBox,
  HeaderIconButton,
  TituloBox,
  UnidadeOrganizacionalBox,
  MenuContaButton,
  MobileLogo,
  ButtonBox,
  MenuAvatar
} from './Header.styles'
import { obterPrimeiroEUltimoNome, obterIniciaisDoNome } from 'utils/format'
import { screenSizes } from 'utils/constants'
import {
  selectPlataformaSelecionada,
  selectMenusPersonalizados,
  selectMenuPlataformas
} from 'store/slices/plataformaSlice'
import { useSelector } from 'react-redux'
import { Plataforma } from 'store/slices/types'
import { MenuConta } from 'components/MenuConta'
import { MenuLateral } from 'components/MenuLateral'
import { Eventos } from 'utils/appInsightsConstants'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HeaderProps, Menu } from './types'

export function Header({ carregandoPlataforma }: HeaderProps) {
  const isAuthenticated = useIsAuthenticated()
  const telaMobile = useMediaQuery(`(max-width:${screenSizes.TABLET})`)

  const plataforma: Plataforma = useSelector(selectPlataformaSelecionada)
  const menuPlataformas = useSelector(selectMenuPlataformas)
  const menus = useSelector(selectMenusPersonalizados)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const menuContaEstaAberto = Boolean(anchorEl)

  const [open, setOpen] = useState(false)

  const { themeName } = useTheme()

  const { accounts } = useMsal()

  const { t } = useTranslation()

  const alterarExibicaoDoMenuLateral = () => {
    setOpen(oldValue => {
      let newValue = !oldValue
      if (newValue) {
        appInsights.trackEvent({
          name: Eventos.HEADER_ABRIR_MENU_LATERAL
        })
      }
      return newValue
    })
  }

  const abrirMenuMinhaConta = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    appInsights.trackEvent({
      name: Eventos.HEADER_ABRIR_MENU_CONTA
    })
  }
  const fecharMenuMinhaConta = () => {
    setAnchorEl(null)
  }

  const nomeUsuario = obterPrimeiroEUltimoNome(accounts[0]?.name || '')
  const iniciaisUsuario = obterIniciaisDoNome(accounts[0]?.name || '')

  const logoConnect = useMemo(
    () => (themeName === 'dark' ? logoConnectDark : logoConnectLight),
    [themeName]
  )
  const logoTituloConnect = useMemo(
    () =>
      themeName === 'dark' ? logoTituloConnectDark : logoTituloConnectLight,
    [themeName]
  )

  const itensMenu: Menu[] = useMemo(
    () => [
      {
        id: '/home',
        rota: '/home',
        label: t('componentes.menu_lateral.inicio')
      },
      { id: '/meu-espaco', rota: '/meu-espaco', label: t('meu-espaco') },
      {
        id: '/plataformas',
        label: t('componentes.menu_lateral.plataformas'),
        itens: [...menuPlataformas]
      },
      {
        id: '/catalogo-itens',
        rota: plataforma.codigo
          ? `/plataformas/${plataforma.codigo}/catalogo-itens`
          : '/catalogo-itens',
        label: t('componentes.menu_lateral.catalogo_itens')
      },
      ...menus
    ],
    [t, menus, menuPlataformas, plataforma.codigo]
  )

  const redirectHome = () => {
    const itemInicio = itensMenu.find(
      item => item.label === t('componentes.menu_lateral.inicio')
    )
    if (itemInicio) {
      const rota = window.location.origin + '/home'
      location.href = rota
    }
  }

  return (
    <>
      {isAuthenticated && (
        <AppBar position="static">
          <HeaderBox>
            <Toolbar disableGutters>
              <HeaderIconButton
                onClick={alterarExibicaoDoMenuLateral}
                data-testid="menu-principal-button"
              >
                <AppsIcon />
              </HeaderIconButton>
              <MenuLateral
                open={open}
                itensMenu={itensMenu}
                onClose={alterarExibicaoDoMenuLateral}
                tituloPlataforma={plataforma.nome}
              />
              {telaMobile && (
                <MobileLogo onClick={redirectHome}>
                  <img
                    src={logoConnect}
                    height={24}
                    alt="Logo Connect"
                    data-testid="logo-connect-mobile"
                    style={{ cursor: 'pointer' }}
                  />
                </MobileLogo>
              )}

              {!telaMobile && (
                <TituloBox>
                  {plataforma.id ? (
                    <UnidadeOrganizacionalBox data-testid="unidade-organizacional">
                      <Tooltip title={t('home_tooltip')}>
                        <ButtonBox onClick={redirectHome}>
                          <img
                            src={logoConnect}
                            height={24}
                            alt="Logo Connect"
                          />
                        </ButtonBox>
                      </Tooltip>
                      <Divider orientation="vertical" />
                      <Typography variant="h1">{plataforma.nome}</Typography>
                    </UnidadeOrganizacionalBox>
                  ) : carregandoPlataforma ? (
                    <Skeleton
                      variant="rectangular"
                      sx={{
                        height: '30px',
                        width: '150px'
                      }}
                    />
                  ) : (
                    <Tooltip title={t('home_tooltip')}>
                      <ButtonBox
                        onClick={redirectHome}
                        data-testid="button-box"
                      >
                        <img
                          src={logoTituloConnect}
                          height={26}
                          alt="Logo Connect"
                          data-testid="logo-titulo-connect"
                        />
                      </ButtonBox>
                    </Tooltip>
                  )}
                </TituloBox>
              )}

              {/* <HeaderIconButton onClick={() => {}}>
                <NotificationsNoneIcon />
              </HeaderIconButton> */}
              {telaMobile && (
                <IconButton
                  id="menu-conta-button"
                  data-testid="menu-conta-mobile-button"
                  aria-controls={menuContaEstaAberto ? 'menu-conta' : undefined}
                  aria-haspopup="true"
                  aria-expanded={menuContaEstaAberto ? 'true' : undefined}
                  onClick={abrirMenuMinhaConta}
                  style={{ marginLeft: 'auto' }}
                >
                  <MenuAvatar>{iniciaisUsuario}</MenuAvatar>
                </IconButton>
              )}

              {!telaMobile && (
                <MenuContaButton
                  id="menu-conta-button"
                  data-testid="menu-conta-button"
                  aria-controls={menuContaEstaAberto ? 'menu-conta' : undefined}
                  aria-haspopup="true"
                  aria-expanded={menuContaEstaAberto ? 'true' : undefined}
                  variant="text"
                  disableElevation
                  onClick={abrirMenuMinhaConta}
                  endIcon={<ArrowDropDownIcon />}
                >
                  {nomeUsuario}
                </MenuContaButton>
              )}
              <MenuConta
                anchorEl={anchorEl}
                menuContaEstaAberto={menuContaEstaAberto}
                fecharMenuMinhaConta={fecharMenuMinhaConta}
                iniciaisUsuario={iniciaisUsuario}
                nomeUsuario={nomeUsuario}
                emailUsuario={accounts[0]?.username}
              />
            </Toolbar>
          </HeaderBox>
        </AppBar>
      )}
    </>
  )
}
